import { useApolloClient } from "@apollo/client";
import {
    GetCustomUpgradesDocument,
    useRequestCustomUpgradeValidationMutation
} from "generated/graphql";
import { GraphQLError } from "graphql";
import { LOADING } from "graphql/helpers/queryHelpers";
import {
    RequestCustomUpgradeValidationSource,
    segmentTrackRequestCustomUpgradeValidation
} from "in_queue/analytics";
import { useCluster } from "in_queue/contexts/ClusterContext";
import { useSubscriptionForCluster } from "in_queue/contexts/ProjectDataContext";
import { getRelevantModelSet } from "in_queue/utils/customUpgradeValidation";
import { useState } from "react";

const UNIQUE_CONSTRAINT_NAME =
    "custom_upgrade_model_set_validations_upgrade_model_set_unique";

export type RequestValidationResult =
    | { readonly ok: true }
    | { readonly ok: false; readonly message: string };

const parseApolloError = (errors: readonly GraphQLError[]): string => {
    const isPermissionDenied = errors.some(
        (gqlError) => gqlError.extensions?.code === "permission-error"
    );
    if (isPermissionDenied) {
        return "You don't have permission to validate upgrades for this team.";
    }
    return `Failed to request validation: ${errors[0].message}`;
};

export interface UseRequestCustomUpgradeValidationResult {
    readonly requestValidation: (
        customUpgradeId: string,
        source: RequestCustomUpgradeValidationSource
    ) => Promise<RequestValidationResult>;
    readonly submitting: boolean;
}

export const useRequestCustomUpgradeValidation =
    (): UseRequestCustomUpgradeValidationResult => {
        const cluster = useCluster();
        const subscription = useSubscriptionForCluster();
        const client = useApolloClient();
        const [requestValidationMutation] =
            useRequestCustomUpgradeValidationMutation();
        // Spans the refetch as well as the mutation. The mutation's own
        // `loading` drops before the new row reaches the UI, which flashes the
        // caller's Validate button back before it switches to "validating".
        const [submitting, setSubmitting] = useState(false);

        const requestValidation = async (
            customUpgradeId: string,
            source: RequestCustomUpgradeValidationSource
        ): Promise<RequestValidationResult> => {
            const modelSet = getRelevantModelSet(
                cluster,
                subscription === LOADING ? undefined : subscription
            );
            if (!modelSet) {
                return {
                    ok: false,
                    message: "There's no current model set to validate against."
                };
            }

            setSubmitting(true);
            try {
                const { errors } = await requestValidationMutation({
                    variables: {
                        customUpgradeId,
                        clusterModelSetId: modelSet.clusterModelSetId
                    },
                    errorPolicy: "all"
                });

                const isAlreadyRequested = (errors ?? []).some(
                    (error) =>
                        error.extensions?.code === "constraint-violation" &&
                        error.message.includes(UNIQUE_CONSTRAINT_NAME)
                );

                if (errors && errors.length > 0 && !isAlreadyRequested) {
                    return { ok: false, message: parseApolloError(errors) };
                }

                // Fetch the new row, or the existing one when the insert was
                // rejected as a duplicate, so the upgrade shows as validating
                // without waiting for the subscription's next push.
                await client.refetchQueries({
                    include: [GetCustomUpgradesDocument]
                });
                if (!isAlreadyRequested) {
                    segmentTrackRequestCustomUpgradeValidation({
                        cluster,
                        upgradeId: customUpgradeId,
                        source
                    });
                }
                return { ok: true };
            } catch {
                return { ok: false, message: "Failed to request validation" };
            } finally {
                setSubmitting(false);
            }
        };

        return { requestValidation, submitting };
    };
